import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRpgStore } from '@/stores/rpgStore';

export function formatCountdown(nextDateStr?: string | Date | null): string {
  if (!nextDateStr) return '';
  const target = new Date(nextDateStr).getTime();
  const diffMs = target - Date.now();
  if (diffMs <= 0) return 'сейчас';
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 60) return `${diffMin} мин`;
  const hours = Math.floor(diffMin / 60);
  const mins = diffMin % 60;
  if (hours < 24) return mins > 0 ? `${hours} ч ${mins} мин` : `${hours} ч`;
  const days = Math.floor(hours / 24);
  return `${days} дн`;
}

interface ComboBadgeProps {
  combo: number;
  xpFloat?: number | null;
}

export function ComboBadge({ combo, xpFloat }: ComboBadgeProps) {
  if (combo < 2 && !xpFloat) return null;

  let label = `🔥 x${combo} Комбо!`;
  let gradient = 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
  let shadow = '0 4px 14px rgba(245, 158, 11, 0.4)';

  if (combo >= 10) {
    label = `💥 x${combo} Не остановить!`;
    gradient = 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)';
    shadow = '0 4px 18px rgba(236, 72, 153, 0.45)';
  } else if (combo >= 5) {
    label = `⚡ x${combo} В огне!`;
    gradient = 'linear-gradient(135deg, #ef4444 0%, #f59e0b 100%)';
    shadow = '0 4px 16px rgba(239, 68, 68, 0.45)';
  }

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        position: 'relative',
        zIndex: 10,
        pointerEvents: 'none',
      }}
    >
      {combo >= 2 && (
        <div
          key={combo}
          className="combo-pop"
          style={{
            background: gradient,
            color: '#ffffff',
            padding: '4px 12px',
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 800,
            boxShadow: shadow,
            letterSpacing: '0.02em',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
          }}
        >
          {label}
        </div>
      )}

      {xpFloat != null && xpFloat > 0 && (
        <div
          key={`xp-${xpFloat}-${combo}`}
          className="xp-float"
          style={{
            position: 'absolute',
            right: -60,
            top: -2,
            background: 'rgba(245, 158, 11, 0.95)',
            color: '#ffffff',
            padding: '2px 8px',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 800,
            boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)',
          }}
        >
          +{xpFloat} XP
        </div>
      )}
    </div>
  );
}

interface RpgProgressBarProps {
  xpGained: number;
}

export function RpgProgressBar({ xpGained }: RpgProgressBarProps) {
  const { character, isLoaded, load } = useRpgStore();
  const [animatedPercent, setAnimatedPercent] = useState<number | null>(null);

  useEffect(() => {
    if (!isLoaded) {
      load();
    }
  }, [isLoaded, load]);

  useEffect(() => {
    if (!character) return;
    const currentPercent = Math.min(100, Math.round((character.xp / character.xp_to_next_level) * 100));
    // Start slightly lower if xp was gained, then animate up
    const prevPercent = xpGained > 0
      ? Math.max(0, Math.min(100, Math.round(((character.xp - xpGained) / character.xp_to_next_level) * 100)))
      : currentPercent;

    setAnimatedPercent(prevPercent);
    const timer = setTimeout(() => {
      setAnimatedPercent(currentPercent);
    }, 200);

    return () => clearTimeout(timer);
  }, [character, xpGained]);

  if (!character) return null;

  const currentPercent = animatedPercent !== null
    ? animatedPercent
    : Math.min(100, Math.round((character.xp / character.xp_to_next_level) * 100));

  return (
    <div
      style={{
        background: 'var(--theme-bg-card, #1a1a2e)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        borderRadius: 16,
        padding: '16px',
        maxWidth: 320,
        margin: '0 auto 24px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.3))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              border: '1px solid rgba(245, 158, 11, 0.4)',
            }}
          >
            🧙‍♂️
          </div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--theme-text)' }}>
              {character.name || 'Герой'}
            </div>
            <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>
              Уровень {character.level}
            </div>
          </div>
        </div>

        {xpGained > 0 && (
          <div
            className="combo-pop"
            style={{
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              color: '#ffffff',
              padding: '3px 10px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 800,
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.35)',
            }}
          >
            +{xpGained} XP
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--theme-text-secondary)', marginBottom: 6 }}>
        <span>Прогресс до Ур. {character.level + 1}</span>
        <span style={{ fontWeight: 600 }}>{character.xp} / {character.xp_to_next_level} XP</span>
      </div>

      <div
        style={{
          width: '100%',
          height: 10,
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: 999,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${currentPercent}%`,
            background: 'linear-gradient(90deg, #f59e0b 0%, #10b981 100%)',
            borderRadius: 999,
            transition: 'width 1s cubic-bezier(0.34, 1.56, 0.64, 1)',
            boxShadow: '0 0 10px rgba(245, 158, 11, 0.5)',
          }}
        />
      </div>
    </div>
  );
}

interface EmptySrsViewProps {
  nextReviewAt?: string | Date | null;
  wordsStudied?: number;
  xpGained?: number;
  onExit: () => void;
  onGoCourses?: () => void;
}

export function EmptySrsView({
  nextReviewAt,
  wordsStudied = 0,
  xpGained = 0,
  onExit,
  onGoCourses,
}: EmptySrsViewProps) {
  const navigate = useNavigate();
  const [countdownText, setCountdownText] = useState(() => formatCountdown(nextReviewAt));

  useEffect(() => {
    if (!nextReviewAt) return;
    setCountdownText(formatCountdown(nextReviewAt));
    const timer = setInterval(() => {
      setCountdownText(formatCountdown(nextReviewAt));
    }, 30000);
    return () => clearInterval(timer);
  }, [nextReviewAt]);

  const handleNavigateCourses = () => {
    if (onGoCourses) {
      onGoCourses();
    } else {
      navigate('/courses');
    }
  };

  return (
    <div
      style={{
        padding: '36px 16px',
        textAlign: 'center',
        maxWidth: 360,
        margin: '0 auto',
      }}
    >
      <div
        className="pulse-glow"
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(59, 130, 246, 0.15))',
          border: '2px solid rgba(16, 185, 129, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 40,
          margin: '0 auto 20px',
        }}
      >
        ✨
      </div>

      <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--theme-text)', marginBottom: 8 }}>
        Все слова повторены!
      </h2>

      <p style={{ fontSize: 14, color: 'var(--theme-text-secondary)', lineHeight: 1.5, marginBottom: 20 }}>
        {wordsStudied > 0
          ? `Отличная сессия! Пройдено ${wordsStudied} слов (+${xpGained} XP). Интервалы повторения обновлены.`
          : 'На сейчас нет слов для повторения. Долгов нет — память надёжно хранит пройденный материал!'}
      </p>

      {countdownText && (
        <div
          style={{
            background: 'var(--theme-bg-card, #1a1a2e)',
            border: '1px solid var(--theme-border)',
            borderRadius: 14,
            padding: '12px 16px',
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            fontSize: 13,
            color: 'var(--theme-text-secondary)',
          }}
        >
          <span>⏳</span>
          <span>
            Следующее повторение через: <strong style={{ color: 'var(--theme-primary, #7c4dff)' }}>{countdownText}</strong>
          </span>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button
          onClick={handleNavigateCourses}
          style={{
            width: '100%',
            padding: '15px 20px',
            background: 'var(--theme-primary)',
            color: 'var(--theme-primary-text)',
            border: 'none',
            borderRadius: 14,
            fontSize: 15,
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          <span>🗺️</span>
          <span>Учить слова в уроках курса →</span>
        </button>

        <button
          onClick={onExit}
          style={{
            width: '100%',
            padding: '13px 20px',
            background: 'var(--theme-bg-elevated)',
            color: 'var(--theme-text-secondary)',
            border: '1px solid var(--theme-border)',
            borderRadius: 14,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          На главную
        </button>
      </div>
    </div>
  );
}

export { DailyGoalVictoryWidget } from './DailyGoalVictoryWidget';

export { SmartInputFeedback } from './SmartInputFeedback';
export type { SmartFeedbackType, SmartInputFeedbackProps } from './SmartInputFeedback';
